/**
 * Réglages d'une trace.
 *
 * Deux niveaux, comme sur Android : un hub qui rassemble les actions, et un
 * sous-dialogue pour les points kilométriques, dont les trois réglages sont
 * liés entre eux et n'ont de sens que validés ensemble.
 *
 * Référence : `TrackSettingsController.kt`.
 */

import { canToggleFileWaypoints, type GpxTrack } from '../core/track'
import type { PreferencesStore } from '../core/preferences'
import type { Translator } from '../i18n'
import { showDialog } from './dialogs'
import { choiceRow, divider, sliderRow, switchRow, textField } from './controls'
import { formatKmForInput, parseKmInput } from './markerController'

/** Bornes du curseur d'espacement, reprises du réglage global. */
const INTERVAL_MIN_KM = 0.5
const INTERVAL_MAX_KM = 10
const INTERVAL_STEP_KM = 0.5

/**
 * Écart en deçà duquel l'intervalle de la trace est considéré égal au global.
 *
 * Le curseur avance par pas de 0,5 km ; un dixième de ce pas suffit à
 * distinguer deux crans sans être sensible aux erreurs de virgule flottante.
 */
const INTERVAL_EPSILON_KM = 0.01

/**
 * Intervalle à enregistrer sur la trace pour une position de curseur donnée.
 *
 * Renvoie 0 — « suivre le réglage global » — quand la valeur choisie est celle
 * du global. La trace continue alors de suivre ce réglage s'il change plus
 * tard, ce qu'une valeur figée identique ne ferait pas.
 */
export function resolveTrackInterval(
  sliderKm: number,
  globalKm: number,
): number {
  return Math.abs(sliderKm - globalKm) < INTERVAL_EPSILON_KM ? 0 : sliderKm
}

/** Position initiale du curseur, bornée à la plage affichable. */
export function initialIntervalKm(
  trackIntervalKm: number,
  globalKm: number,
): number {
  const base = trackIntervalKm > 0 ? trackIntervalKm : globalKm
  return Math.min(Math.max(base, INTERVAL_MIN_KM), INTERVAL_MAX_KM)
}

export interface TrackSettingsCallbacks {
  /** Le rendu de la trace a changé : couches, tiroir, persistance. */
  readonly onTrackChanged: (track: GpxTrack) => void
  readonly onRename: (track: GpxTrack) => void
  readonly onPickColor: (track: GpxTrack) => void
  readonly onShowMarkers: (track: GpxTrack) => void
  /** Matérialise en marqueurs les radars proches de la trace. */
  readonly onCheckRadars: (track: GpxTrack) => void
  /** La base de radars est-elle disponible localement ? */
  readonly hasRadarData: () => boolean
  /** Récupère les altitudes manquantes de la trace. */
  readonly onFetchElevation: (track: GpxTrack) => void
  /** La trace gagnerait-elle à une récupération d'altitude ? */
  readonly needsElevation: (track: GpxTrack) => boolean
  /** Bascule entre tracé d'origine et itinéraire routier. */
  readonly onToggleRoute: (track: GpxTrack) => void
  /**
   * Une autre trace de la bibliothèque partage-t-elle le même fichier source ?
   *
   * C'est ce comptage de références qui dit qui possède les points d'intérêt du
   * fichier. `null` quand la trace n'a pas de copie source.
   */
  readonly sharesFileWithOthers: (track: GpxTrack) => boolean | null
  /** Choisit à quel point kilométrique se trouve la position accrochée. */
  readonly onFixPosition: (track: GpxTrack) => void
  /**
   * La correction a-t-elle de quoi s'exercer sur cette trace ?
   *
   * Vrai seulement si la position y est accrochée et que la trace y repasse :
   * un seul point candidat ne laisse rien à corriger.
   */
  readonly canFixPosition: (track: GpxTrack) => boolean
  /**
   * Prend ou rend les points d'intérêt du fichier source.
   *
   * Les reprendre demande de relire le fichier — ils ne sont pas gardés en
   * mémoire —, d'où la promesse ; elle rend `false` quand le fichier n'a pas
   * pu être relu, et l'interrupteur revient alors sur sa position.
   */
  readonly onToggleFileWaypoints: (
    track: GpxTrack,
    include: boolean,
  ) => Promise<boolean>
}

export class TrackSettingsScreen {
  constructor(
    private readonly t: Translator,
    private readonly prefs: PreferencesStore,
    private readonly callbacks: TrackSettingsCallbacks,
  ) {}

  /** Hub des réglages d'une trace. */
  show(track: GpxTrack): void {
    const body = document.createElement('div')
    body.className = 'track-settings'

    const close = showDialog(document.body, {
      title: this.t.t('track_settings_title', { name: track.name }),
      body,
      actions: [{ label: this.t.t('close') }],
      dismissible: true,
    })

    const row = (
      label: string,
      onOpen: () => void,
      trailing?: HTMLElement,
    ): HTMLElement => {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'setting-row setting-row--clickable'

      const text = document.createElement('span')
      text.className = 'setting-row__label'
      text.textContent = label
      button.append(text)

      if (trailing) button.append(trailing)

      button.addEventListener('click', () => {
        // Le hub se referme avant d'ouvrir le sous-dialogue : deux boîtes
        // empilées rendraient le fond ambigu et le retour arrière incertain.
        close()
        onOpen()
      })
      return button
    }

    const swatch = document.createElement('span')
    swatch.className = 'track-settings__swatch'
    swatch.style.background = track.color

    body.append(
      row(this.t.t('rename_track'), () => this.callbacks.onRename(track)),
      row(this.t.t('change_color'), () => this.callbacks.onPickColor(track), swatch),
      row(this.t.t('track_settings_km_row'), () => this.showKmSettings(track)),
      row(this.t.t('custom_markers'), () => this.callbacks.onShowMarkers(track)),
    )

    // Itinéraire routier : la même ligne bascule dans les deux sens, son
    // libellé disant ce qu'elle va faire.
    //
    // Sauf pour une trace dont le **fichier** se déclarait déjà routé : ses
    // `<trkseg>` sont le résultat d'un calcul, il n'y a aucun tracé d'origine
    // à relire. Proposer « revenir » n'aurait rien à rendre et échouerait sur
    // un message d'erreur ; c'est le recalcul qui a un sens.
    const canRevert = track.isRouted && !track.fileDeclaresRouted
    body.append(
      divider(),
      choiceRow({
        label: this.t.t(
          canRevert ? 'action_revert_waypoints' : 'action_calculate_route',
        ),
        value: '',
        onOpen: () => {
          close()
          this.callbacks.onToggleRoute(track)
        },
      }),
    )

    // Récupération d'altitude : proposée seulement quand elle apporterait
    // quelque chose. Une trace qui porte déjà un profil n'a rien à y gagner,
    // et la ligne n'aurait aucun effet visible.
    if (this.callbacks.needsElevation(track)) {
      body.append(
        divider(),
        choiceRow({
          label: this.t.t('elevation_fetch'),
          value: '',
          onOpen: () => {
            close()
            this.callbacks.onFetchElevation(track)
          },
        }),
      )
    }

    // Vérification des radars : proposée en permanence, mais inopérante sans
    // base locale — grisée avec la marche à suivre en sous-titre, comme sur
    // Android, plutôt que masquée sans explication.
    const hasRadars = this.callbacks.hasRadarData()
    body.append(
      divider(),
      choiceRow({
        label: this.t.t('radar_check'),
        value: '',
        disabled: !hasRadars,
        summary: hasRadars ? undefined : this.t.t('radar_check_need_db'),
        onOpen: () => {
          close()
          this.callbacks.onCheckRadars(track)
        },
      }),
    )

    // Correction de position : dernière action du hub, comme sur Android, et
    // grisée avec sa condition en sous-titre. Elle ne sert qu'en roulant, sur
    // une trace qui repasse au même endroit — l'afficher en permanence
    // apprend au moins qu'elle existe et à quel moment y revenir.
    const canFix = this.callbacks.canFixPosition(track)
    body.append(
      divider(),
      choiceRow({
        label: this.t.t('fix_position'),
        value: '',
        disabled: !canFix,
        summary: canFix ? undefined : this.t.t('fix_position_not_available'),
        onOpen: () => {
          close()
          this.callbacks.onFixPosition(track)
        },
      }),
    )

    if (canToggleFileWaypoints(track, this.callbacks.sharesFileWithOthers(track))) {
      body.append(
        divider(),
        switchRow({
          label: this.t.t('track_settings_show_waypoints'),
          get: () => track.waypoints.length > 0,
          // Rallumer relit le fichier source : l'attente est réelle, et le
          // refus possible — d'où la promesse, que `switchRow` sait attendre
          // puis démentir.
          set: (value) => this.callbacks.onToggleFileWaypoints(track, value),
          onChange: () => this.callbacks.onTrackChanged(track),
        }),
      )
    }
  }

  /**
   * Départ réel, espacement des points kilométriques et référence de comptage.
   *
   * Les trois valeurs ne sont lues qu'à la validation, jamais à la volée : le
   * départ réel décale toute la numérotation, et l'appliquer à chaque frappe
   * ferait sauter les marqueurs sous les doigts de l'utilisateur.
   */
  showKmSettings(track: GpxTrack): void {
    const body = document.createElement('div')

    const globalIntervalKm = this.prefs.get('kmMarkerInterval')

    /* --- Départ réel --- */

    const realStartKm = track.realStartDistance / 1000
    const real = textField({
      label: this.t.t('real_start_distance'),
      value: realStartKm > 0 ? formatKmForInput(realStartKm) : '',
      inputMode: 'decimal',
    })
    real.input.placeholder = '0.0'

    const realRow = document.createElement('div')
    realRow.className = 'field-row'

    const resetReal = document.createElement('button')
    resetReal.type = 'button'
    resetReal.className = 'button button--outlined'
    resetReal.textContent = this.t.t('reset')
    resetReal.addEventListener('click', () => {
      real.input.value = ''
      real.input.dispatchEvent(new Event('input'))
    })

    realRow.append(real.element, resetReal)
    body.append(realRow, divider())

    /* --- Espacement propre à la trace --- */

    const heading = document.createElement('div')
    heading.className = 'setting-heading'
    heading.textContent = this.t.t('km_markers')

    // Un intervalle nul signifie « suivre le global » : le curseur part alors
    // de la valeur globale, mais rien n'est propre à la trace tant qu'on n'y
    // touche pas.
    let intervalKm = initialIntervalKm(track.kmMarkerInterval, globalIntervalKm)

    const resetInterval = document.createElement('button')
    resetInterval.type = 'button'
    resetInterval.className = 'button button--outlined'
    resetInterval.textContent = this.t.t('reset')

    const isGlobal = (value: number): boolean =>
      Math.abs(value - globalIntervalKm) < INTERVAL_EPSILON_KM

    const syncReset = (value: number): void => {
      // Le bouton n'apparaît que lorsque la trace s'écarte du réglage global :
      // proposer de revenir à une valeur déjà en place n'aurait aucun effet
      // visible et laisserait croire à une panne.
      resetInterval.hidden = isGlobal(value)
    }

    const slider = sliderRow({
      label: this.t.t('km_marker_custom_interval'),
      min: INTERVAL_MIN_KM,
      max: INTERVAL_MAX_KM,
      step: INTERVAL_STEP_KM,
      get: () => intervalKm,
      set: (value) => {
        intervalKm = value
      },
      format: (value) => this.t.t('km_marker_interval_value', {
        value: this.t.number(value, 1),
      }),
      onChange: () => syncReset(intervalKm),
    })

    const range = slider.querySelector('input')
    resetInterval.addEventListener('click', () => {
      intervalKm = Math.min(
        Math.max(globalIntervalKm, INTERVAL_MIN_KM),
        INTERVAL_MAX_KM,
      )
      if (range) {
        range.value = String(intervalKm)
        range.dispatchEvent(new Event('input'))
      }
      syncReset(intervalKm)
    })

    syncReset(intervalKm)
    body.append(heading, slider, resetInterval, divider())

    /* --- Référence de comptage --- */

    let fromFictive = track.kmMarkersFromFictiveStart
    const fictive = switchRow({
      label: this.t.t('km_from_fictive_start'),
      get: () => fromFictive,
      set: (value) => {
        fromFictive = value
      },
    })

    const fictiveInput = fictive.querySelector('input')

    // Sans départ réel, il n'y a pas de départ fictif : l'interrupteur suit la
    // saisie en direct plutôt que d'attendre la validation, sans quoi il
    // resterait actif alors que le champ vient d'être vidé.
    const syncFictive = (): void => {
      const km = parseKmInput(real.input.value) ?? 0
      const enabled = km > 0
      if (!fictiveInput) return
      fictiveInput.disabled = !enabled
      if (!enabled) {
        fictiveInput.checked = false
        fromFictive = false
      }
      fictive.style.opacity = enabled ? '1' : '0.5'
    }

    real.input.addEventListener('input', syncFictive)
    syncFictive()

    body.append(fictive)

    showDialog(document.body, {
      title: this.t.t('track_settings_km_row'),
      body,
      dismissible: true,
      actions: [
        { label: this.t.t('cancel') },
        {
          label: this.t.t('ok'),
          primary: true,
          onClick: () => {
            const km = parseKmInput(real.input.value)
            track.realStartDistance = km !== null && km > 0 ? km * 1000 : 0
            track.kmMarkerInterval = resolveTrackInterval(
              intervalKm,
              globalIntervalKm,
            )
            track.kmMarkersFromFictiveStart =
              track.realStartDistance > 0 && fromFictive

            this.callbacks.onTrackChanged(track)
          },
        },
      ],
    })
  }
}
